import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { bonusTypeLabels } from '../utils/bonus';
import LoadErrorScreen from './LoadErrorScreen';

const BonusPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [error, setError] = useState('');
  const [balance, setBalance] = useState(0);
  const [rules, setRules] = useState(null);
  const [transactions, setTransactions] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    const load = async () => {
      setLoading(true);
      setLoadError(false);
      setError('');
      try {
        const res = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/bonus`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.message || 'Не удалось загрузить бонусы');
        setBalance(data.balance || 0);
        setRules(data.rules || null);
        setTransactions(data.transactions || []);
      } catch (e) {
        setLoadError(true);
        setError(e.message || 'Ошибка загрузки');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [navigate]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: '#8B7D6B' }} />
      </Box>
    );
  }

  if (loadError) {
    return <LoadErrorScreen />;
  }

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
        Бонусный кошелёк
      </Typography>

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #8B7D6B 0%, #6B5D4B 100%)', color: '#fff' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <AccountBalanceWalletIcon />
            <Typography variant="overline" sx={{ opacity: 0.9 }}>
              Доступно бонусов
            </Typography>
          </Box>
          <Typography variant="h3" sx={{ fontWeight: 700 }}>
            {Number(balance).toLocaleString('ru-RU')} ₽
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
            1 бонус = 1 рубль
          </Typography>
        </CardContent>
      </Card>

      <Paper sx={{ p: 2, mb: 3, backgroundColor: 'grey.50' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          Как работают бонусы
        </Typography>
        <Box component="ul" sx={{ m: 0, pl: 2.5, color: 'text.secondary' }}>
          <li>
            После завершения аренды на счёт начисляется{' '}
            {rules?.accrualPercent || 15}% от суммы аренды бонусами.
          </li>
          <li>
            При оформлении нового заказа можно оплатить до{' '}
            {rules?.maxSpendPercent || 30}% суммы аренды бонусами.
          </li>
          <li>Залог оплачивается только деньгами, бонусами покрывается только аренда.</li>
        </Box>
      </Paper>

      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>
        История операций
      </Typography>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Дата</TableCell>
              <TableCell>Операция</TableCell>
              <TableCell>Сумма</TableCell>
              <TableCell>Баланс после</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {transactions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    Пока нет операций с бонусами
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              transactions.map((tx) => (
                <TableRow key={tx._id}>
                  <TableCell>
                    {tx.createdAt ? new Date(tx.createdAt).toLocaleString('ru-RU') : '—'}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {bonusTypeLabels[tx.type] || tx.type}
                    </Typography>
                    {tx.note && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {tx.note}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ color: tx.amount >= 0 ? 'success.main' : 'error.main', fontWeight: 600 }}>
                    {tx.amount >= 0 ? '+' : ''}
                    {Number(tx.amount).toLocaleString('ru-RU')} ₽
                  </TableCell>
                  <TableCell>{Number(tx.balanceAfter || 0).toLocaleString('ru-RU')} ₽</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export default BonusPage;
